import { DepartmentCountryCatalog } from "./addressLine";
import {
  BENELUX_COUNTRY_CODES,
  BENELUX_PALLET_TYPES,
  BENELUX_ROUTE_LOCATION_HINT,
  DEFAULT_PALLET_TYPES,
  ROUTE_LOCATION_HINT,
  SERVICE_PILL_LABELS,
  SERVICE_PILL_ORDER,
  type Country,
} from "./constants";
import type { DepartmentConfig, FormState, PalletType, ServiceKey } from "./types";

export type DepartmentFieldId =
  | "loadCountry"
  | "loadPostal"
  | "loadCity"
  | "loadStreet"
  | "loadHouseNumber"
  | "unloadCountry"
  | "unloadPostal"
  | "unloadCity"
  | "unloadStreet"
  | "unloadHouseNumber"
  | "pickupDate"
  | "unloadDate"
  | "totalWeight"
  | "goods"
  | "loadReference"
  | "unloadReference"
  | "reference"
  | "temperature";

const HOME_COUNTRY_BY_SLUG: Record<string, string> = {
  albania: "AL",
  al: "AL",
  austria: "AT",
  at: "AT",
  belgium: "BE",
  be: "BE",
  bosnia: "BA",
  ba: "BA",
  bulgaria: "BG",
  bg: "BG",
  croatia: "HR",
  hr: "HR",
  cyprus: "CY",
  cy: "CY",
  "czech-republic": "CZ",
  cz: "CZ",
  denmark: "DK",
  dk: "DK",
  england: "EN",
  en: "EN",
  estonia: "EE",
  ee: "EE",
  finland: "FI",
  fi: "FI",
  france: "FR",
  fr: "FR",
  georgia: "GE",
  ge: "GE",
  germany: "DE",
  de: "DE",
  greece: "GR",
  gr: "GR",
  hungary: "HU",
  hu: "HU",
  iceland: "IS",
  is: "IS",
  ireland: "IE",
  ie: "IE",
  italy: "IT",
  it: "IT",
  kazakhstan: "KZ",
  kz: "KZ",
  kosovo: "KO",
  ko: "KO",
  latvia: "LV",
  lv: "LV",
  lithuania: "LT",
  lt: "LT",
  luxembourg: "LU",
  lu: "LU",
  malta: "MT",
  mt: "MT",
  montenegro: "ME",
  me: "ME",
  netherlands: "NL",
  nl: "NL",
  "northern-macedonia": "MK",
  mk: "MK",
  norway: "NO",
  no: "NO",
  poland: "PL",
  pl: "PL",
  portugal: "PT",
  pt: "PT",
  romania: "RO",
  ro: "RO",
  scotland: "SC",
  sc: "SC",
  serbia: "RS",
  rs: "RS",
  slovakia: "SK",
  sk: "SK",
  slovenia: "SI",
  si: "SI",
  spain: "ES",
  es: "ES",
  sweden: "SE",
  se: "SE",
  switzerland: "CH",
  ch: "CH",
  turkey: "TR",
  tr: "TR",
  turkmenistan: "TM",
  tm: "TM",
  ukraine: "UA",
  ua: "UA",
  "united-kingdom": "GB",
  uk: "GB",
  gb: "GB",
  uzbekistan: "UZ",
  uz: "UZ",
};

export class DepartmentHomeCountry {
  code(slug: string): string {
    return HOME_COUNTRY_BY_SLUG[slug.trim().toLowerCase()] ?? "";
  }
}

export class DepartmentFieldAccess {
  constructor(private readonly config: DepartmentConfig) {}

  homeCountry(): string {
    return new DepartmentHomeCountry().code(this.config.slug);
  }

  countriesForSide(otherCountry: string): Country[] {
    const listed = this.countries();
    const home = this.homeCountry();
    if (!home) return listed;
    const other = otherCountry.trim().toUpperCase();
    if (!other || other === home) return listed;
    return listed.filter((country) => country.c === home);
  }

  isRequired(id: DepartmentFieldId): boolean {
    return this.field(id)?.required === true;
  }

  isPresent(id: DepartmentFieldId): boolean {
    const field = this.field(id);
    return !!field && field.visible !== false;
  }

  private field(id: DepartmentFieldId) {
    switch (id) {
      case "loadCountry":
        return this.config.fields.loadCountry;
      case "loadPostal":
        return this.config.fields.loadPostal;
      case "loadCity":
        return this.config.fields.loadCity;
      case "loadStreet":
        return this.config.fields.loadStreet;
      case "loadHouseNumber":
        return this.config.fields.loadHouseNumber;
      case "unloadCountry":
        return this.config.fields.unloadCountry;
      case "unloadPostal":
        return this.config.fields.unloadPostal;
      case "unloadCity":
        return this.config.fields.unloadCity;
      case "unloadStreet":
        return this.config.fields.unloadStreet;
      case "unloadHouseNumber":
        return this.config.fields.unloadHouseNumber;
      case "pickupDate":
        return this.config.fields.pickupDate;
      case "unloadDate":
        return this.config.fields.unloadDate;
      case "totalWeight":
        return this.config.fields.totalWeight;
      case "goods":
        return this.config.fields.goods;
      case "loadReference":
        return this.config.fields.loadReference;
      case "unloadReference":
        return this.config.fields.unloadReference;
      case "reference":
        return this.config.fields.reference;
      case "temperature":
        return this.config.fields.temperature;
    }
  }

  palletTypes(): PalletType[] {
    const listed = this.config.palletTypes;
    if (listed && listed.length > 0) return listed;
    return DEFAULT_PALLET_TYPES;
  }

  countryCatalog(): DepartmentCountryCatalog {
    return DepartmentCountryCatalog.fromCodes(this.config.countries);
  }

  countries(): Country[] {
    return this.countryCatalog().list();
  }

  hasExtendedReferences(): boolean {
    return this.isPresent("loadReference") || this.isPresent("unloadReference");
  }

  routeHint(): string {
    return this.isPresent("loadStreet") ? BENELUX_ROUTE_LOCATION_HINT : ROUTE_LOCATION_HINT;
  }

  serviceLabel(key: ServiceKey): string {
    if (key === "ref" && this.hasExtendedReferences()) return "References";
    return SERVICE_PILL_LABELS[key];
  }

  showsTimeDeliveryDeadline(): boolean {
    return !this.isPresent("unloadDate");
  }

  showsTemperature(): boolean {
    const field = this.field("temperature");
    return !field || field.visible !== false;
  }

  visibleServiceKeys(): ServiceKey[] {
    return SERVICE_PILL_ORDER.filter((key) => key !== "temp" || this.showsTemperature());
  }

  requiresReferences(): boolean {
    return (
      this.isRequired("reference") ||
      this.isRequired("loadReference") ||
      this.isRequired("unloadReference")
    );
  }

  lockedServiceKeys(): ServiceKey[] {
    return this.requiresReferences() ? ["ref"] : [];
  }
}

export class DepartmentFormDefaults {
  constructor(private readonly fields: DepartmentFieldAccess) {}

  apply(form: FormState): FormState {
    if (!this.fields.requiresReferences() || form.ref.active) return form;
    return { ...form, ref: { ...form.ref, active: true } };
  }
}

function beneluxFallbackFields(): DepartmentConfig["fields"] {
  const required = { required: true };
  const optional = { required: false };
  return {
    loadCountry: required,
    loadPostal: required,
    loadCity: required,
    loadStreet: optional,
    loadHouseNumber: optional,
    unloadCountry: required,
    unloadPostal: required,
    unloadCity: required,
    unloadStreet: optional,
    unloadHouseNumber: optional,
    pickupDate: optional,
    unloadDate: optional,
    totalWeight: required,
    goods: optional,
    reference: optional,
    loadReference: optional,
    unloadReference: optional,
    temperature: { required: false, visible: false },
  };
}

export function beneluxFallbackConfig(): DepartmentConfig {
  return {
    slug: "benelux",
    title: "Benelux Quote Request",
    flow: ["form", "upload"],
    fields: beneluxFallbackFields(),
    palletTypes: BENELUX_PALLET_TYPES,
    countries: [...BENELUX_COUNTRY_CODES],
    ui: {
      headerLabel: "Benelux Quote Request",
      destinationEmail: "",
    },
  };
}
